import { useEffect, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const empty = {
  name: "",
  contact_person: "",
  contact_phone: "",
  contact_email: "",
  address: "",
};

export default function Clients() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    const r = await api.get("/clients");
    setRows(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await api.put(`/clients/${editing}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/clients", form);
        toast.success("Client added");
      }
      setOpen(false);
      setForm(empty);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const edit = (row) => {
    setForm({
      name: row.name,
      contact_person: row.contact_person || "",
      contact_phone: row.contact_phone || "",
      contact_email: row.contact_email || "",
      address: row.address || "",
    });
    setEditing(row.id);
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif font-bold text-2xl text-[#1A3A5C]">Clients</h2>
          <p className="text-sm text-[#475569]">
            Companies you supply manpower to
          </p>
        </div>
        <Dialog
          open={open}
          onOpenChange={(o) => {
            setOpen(o);
            if (!o) {
              setEditing(null);
              setForm(empty);
            }
          }}
        >
          <DialogTrigger asChild>
            <Button
              data-testid="new-client-btn"
              className="bg-[#1A3A5C] hover:bg-[#0F2236]"
            >
              <Plus className="w-4 h-4 mr-1" /> New Client
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif">
                {editing ? "Edit Client" : "New Client"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div>
                <Label>Name *</Label>
                <Input
                  data-testid="client-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Contact Person</Label>
                  <Input
                    value={form.contact_person}
                    onChange={(e) =>
                      setForm({ ...form, contact_person: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={form.contact_phone}
                    onChange={(e) =>
                      setForm({ ...form, contact_phone: e.target.value })
                    }
                  />
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  value={form.contact_email}
                  onChange={(e) =>
                    setForm({ ...form, contact_email: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>Address</Label>
                <Textarea
                  rows={2}
                  value={form.address}
                  onChange={(e) =>
                    setForm({ ...form, address: e.target.value })
                  }
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="save-client"
                className="w-full bg-[#1A3A5C] hover:bg-[#0F2236]"
              >
                {loading ? "Saving..." : "Save"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="px-4 pt-3 pb-2 border-b bg-slate-50/50 flex items-center justify-between gap-3">
          <Input
            data-testid="clients-search"
            placeholder="Search by name, contact, phone, email, address…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-md"
          />
          <div className="text-xs text-[#475569]">
            {(() => {
              const ql = q.trim().toLowerCase();
              const c = ql
                ? rows.filter((r) =>
                    [r.name, r.contact_person, r.contact_phone, r.contact_email, r.address]
                      .join(" ").toLowerCase().includes(ql)
                  ).length
                : rows.length;
              return `${c} / ${rows.length}`;
            })()}
          </div>
        </div>
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs uppercase tracking-wider text-[#475569]">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Address</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.length === 0 && (
              <tr>
                <td colSpan="5" className="px-4 py-12 text-center text-[#475569]">
                  No clients yet. Add your first client.
                </td>
              </tr>
            )}
            {(() => {
              const ql = q.trim().toLowerCase();
              const filtered = ql
                ? rows.filter((r) =>
                    [r.name, r.contact_person, r.contact_phone, r.contact_email, r.address]
                      .join(" ").toLowerCase().includes(ql)
                  )
                : rows;
              if (rows.length > 0 && filtered.length === 0) {
                return (
                  <tr>
                    <td colSpan="5" className="px-4 py-12 text-center text-[#475569]">
                      No results for "{q}".
                    </td>
                  </tr>
                );
              }
              return filtered.map((r) => (
              <tr key={r.id} data-testid={`client-row-${r.id}`}>
                <td className="px-4 py-3 font-semibold text-[#1A3A5C]">{r.name}</td>
                <td className="px-4 py-3 text-sm">{r.contact_person || "—"}</td>
                <td className="px-4 py-3 text-sm">{r.contact_phone || "—"}</td>
                <td className="px-4 py-3 text-sm text-[#475569]">{r.address || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => edit(r)}
                    className="text-[#1A3A5C] hover:bg-slate-100 p-2 rounded"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(r.id)}
                    data-testid={`delete-client-${r.id}`}
                    className="text-red-600 hover:bg-red-50 p-2 rounded ml-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
              ));
            })()}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
